"use client";

import type React from "react";
import { useMemo, useState } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Pencil } from "lucide-react";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { projectHumanMarkdown, projectReadingMarkdown, type ReadingMarkdownProjection } from "@/lib/humanMarkdown";
import { nextHeadingId } from "@/lib/markdownOutline";
import { ImageAnnotator } from "@/components/ui/ImageAnnotator";
import type { Issue } from "@/lib/types";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISSUE_IDENTIFIER = /^[A-Z][A-Z0-9]*-[1-9][0-9]*$/i;
function decodeInternalTarget(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return decoded && !/[\u0000-\u001f\u007f]/.test(decoded) ? decoded : null;
  } catch { return null; }
}
/** These schemes are consumed by internal renderers, never browser href/src. */
function internalMarkdownUrl(value: string): string | undefined {
  const human = /^pauta:user:(.*)$/i.exec(value);
  if (human) return UUID.test(human[1]) ? value.toLowerCase() : "";
  const match = /^(wikilink|issue|doc|project|cycle|user|tag):(.*)$/i.exec(value);
  if (!match) return undefined;
  const target = decodeInternalTarget(match[2]);
  const kind = match[1].toLowerCase();
  if (!target || (kind === "doc" && !UUID.test(target)) ||
    (kind === "issue" && !UUID.test(target) && !ISSUE_IDENTIFIER.test(target))) return "";
  return `${kind}:${match[2]}`;
}

type IssueMarkdownNode = {
  type: string;
  value?: string;
  url?: string;
  children?: IssueMarkdownNode[];
  position?: { start: { offset?: number }; end: { offset?: number } };
};
/** Resolve bare identifiers only in human prose, never inside links or code. */
function remarkBareIssueIdentifiers({ prefixes = [] }: { prefixes?: readonly string[] } = {}) {
  const namespaces = new Set(prefixes.filter((prefix) => typeof prefix === "string").map((prefix) => prefix.toUpperCase()).filter((prefix) => /^[A-Z][A-Z0-9]*$/.test(prefix)));
  return (tree: unknown, file: { value?: unknown }) => {
    if (!namespaces.size) return;
    const source = String(file.value ?? "");
    const htmlRanges = [...source.matchAll(/<([a-z][\w:-]*)\b[^>]*>[\s\S]*?<\/\1\s*>/gi)]
      .map((match) => ({ start: match.index!, end: match.index! + match[0].length }));
    function textParts(node: IssueMarkdownNode): IssueMarkdownNode[] {
      const value = node.value ?? "";
      // CommonMark removes authored escapes from text nodes. Keep such text
      // literal so an escaped identifier cannot become a new reference.
      const start = node.position?.start.offset;
      const end = node.position?.end.offset;
      if (start !== undefined && end !== undefined && (source.slice(start, end).includes("\\") ||
        htmlRanges.some((range) => start < range.end && end > range.start))) return [node];
      const opaqueTokens = [...value.matchAll(/\S+/g)].filter(([token]) =>
        /(?:[a-z][a-z0-9+.-]*:[^\s<>"']+|www\.|[/\\]|[^\s@]+@[^\s@]+)/i.test(token));
      const parts: IssueMarkdownNode[] = [];
      let cursor = 0;
      for (const match of value.matchAll(/(?<![\p{L}\p{N}_@#\\/.-])([A-Z][A-Z0-9]*-[1-9][0-9]*)(?![\p{L}\p{N}_@\\/-]|\.[\p{L}\p{N}])/giu)) {
        const identifier = match[1];
        const index = match.index!;
        const finish = index + identifier.length;
        if (!namespaces.has(identifier.slice(0, identifier.lastIndexOf("-")).toUpperCase()) ||
          opaqueTokens.some((token) => index >= token.index! && index < token.index! + token[0].length)) continue;
        if (index > cursor) parts.push({ type: "text", value: value.slice(cursor, index) });
        parts.push({ type: "link", url: `issue:${identifier.toUpperCase()}`, children: [{ type: "text", value: identifier }] });
        cursor = finish;
      }
      if (!parts.length) return [node];
      if (cursor < value.length) parts.push({ type: "text", value: value.slice(cursor) });
      return parts;
    }
    function visit(node: IssueMarkdownNode) {
      if (["link", "linkReference", "image", "imageReference", "code", "inlineCode", "html"].includes(node.type) || !node.children) return;
      node.children = node.children.flatMap((child) => {
        if (child.type === "text") return textParts(child);
        visit(child);
        return [child];
      });
    }
    visit(tree as IssueMarkdownNode);
  };
}

/**
 * Markdown renderer with Pauta extensions:
 * - `[[Doc title]]` → wikilinks (internal doc navigation)
 * - `@ABC-123`     → issue mention (link to issue in current workspace)
 * - `@project:Name`→ project mention
 * - `@cycle:Name`  → cycle mention
 * - `#tag-name`    → tag page (aggregates all entities with that tag)
 *
 * All patterns get rewritten at the string level into standard Markdown
 * links with special href schemes (`wikilink:`, `issue:`, `project:`,
 * `cycle:`, `tag:`). A custom `a` renderer intercepts them and turns them
 * into in-app navigation.
 */
export function Markdown({
  content,
  className,
  onImageAnnotated,
  headingIdPrefix = "",
  readingTitle,
  referencedIssues = [],
  recognizeBareIssues = true,
}: {
  content: string;
  className?: string;
  headingIdPrefix?: string;
  readingTitle?: string;
  /** Locally resolved, authorized references; canonical store rows win by ID. */
  referencedIssues?: readonly Issue[];
  /** Older preserved versions remain literal rather than creating new references. */
  recognizeBareIssues?: boolean;
  /**
   * If provided, every image renders with a hover-revealed ✏️ button that
   * opens the ImageAnnotator. On save, this callback fires with
   * (oldUrl, newUrl) and the parent should replace the URL in its
   * markdown source. Read-only renders (comments, sub-doc previews)
   * should omit this prop. PAUT-67.
   */
  onImageAnnotated?: (oldUrl: string, newUrl: string) => void;
}) {
  const docs = useStore((s) => s.docs);
  const storedIssues = useStore((s) => s.issues);
  const projects = useStore((s) => s.projects);
  const cycles = useStore((s) => s.cycles);
  const users = useStore((s) => s.users);
  const teams = useStore((s) => s.teams) ?? [];
  const workspaceMembers = useStore((s) => s.workspaceMembers) ?? [];
  const workspaces = useStore((s) => s.workspaces) ?? [];
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const selectDoc = useStore((s) => s.selectDoc);
  const selectIssue = useStore((s) => s.selectIssue);
  const setActiveTag = useStore((s) => s.setActiveTag);
  const issues = useMemo(() => {
    const byId = new Map(referencedIssues.filter((issue) => issue.workspaceId === activeWorkspaceId).map((issue) => [issue.id, issue]));
    for (const issue of storedIssues) if (issue.workspaceId === activeWorkspaceId) byId.set(issue.id, issue);
    return [...byId.values()];
  }, [referencedIssues, storedIssues, activeWorkspaceId]);
  const issuePrefixes = useMemo(() => recognizeBareIssues
    ? teams.filter((team) => team.workspaceId === activeWorkspaceId).map((team) => team.key)
    : [], [teams, activeWorkspaceId, recognizeBareIssues]);
  const workspaceSlug = workspaces.find((workspace) => workspace.id === activeWorkspaceId)?.slug;
  const workspacePath = workspaceSlug ? `/${encodeURIComponent(workspaceSlug)}`
    : typeof window !== "undefined" && window.location.href ? new URL(window.location.href).pathname : "/";
  const entityHref = (kind: "issue" | "doc", id: string) => `${workspacePath}?${kind}=${encodeURIComponent(id)}${kind === "doc" ? "#docs" : ""}`;
  function selectInternal(event: React.MouseEvent<HTMLAnchorElement>, action: () => void) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    action();
  }

  const projection = useMemo<ReadingMarkdownProjection>(() => {
    if (readingTitle !== undefined) return projectReadingMarkdown(content, readingTitle);
    const human = projectHumanMarkdown(content);
    return { ...human, generated: false, presentationChanged: false, originalBody: human.body };
  }, [content, readingTitle]);
  const [historySource, setHistorySource] = useState<string | null>(null);
  const historyOpen = historySource === content;
  const transformed = preprocess(projection.body);

  // Index of doc titles in the active workspace, normalized for fuzzy match
  // (lowercase, leading emoji+space stripped, collapsed whitespace). Used by
  // the table-cell and list-item renderers to auto-link plain-text references
  // to existing docs — fixes the common case where an agent writes a KB index
  // in a markdown table without wrapping each cell in [[]].
  const docByNormalizedTitle = new Map<string, { id: string; title: string }>();
  for (const d of docs) {
    if (d.workspaceId !== activeWorkspaceId) continue;
    const norm = normalizeDocTitle(d.title);
    if (norm.length >= 3) docByNormalizedTitle.set(norm, { id: d.id, title: d.title });
  }

  function tryAutoLink(text: string): React.ReactNode {
    if (!text || text.length > 80) return text;
    const trimmed = text.trim();
    if (trimmed.length < 3) return text;
    const target = docByNormalizedTitle.get(normalizeDocTitle(trimmed));
    if (!target) return text;
    return (
      <a
        href={entityHref("doc", target.id)}
        onClick={(event) => selectInternal(event, () => selectDoc(target.id))}
        className="text-[var(--accent)] hover:underline"
        title={`Open ${target.title}`}
      >
        {text}
      </a>
    );
  }

  // Recursively walk children: when a child is a plain string and matches a
  // doc title, replace it with a link. Anything else passes through unchanged.
  function autoLinkChildren(children: React.ReactNode): React.ReactNode {
    if (typeof children === "string") return tryAutoLink(children);
    if (Array.isArray(children)) {
      return children.map((c, i) => {
        if (typeof c === "string") {
          const linked = tryAutoLink(c);
          return typeof linked === "string" ? c : <span key={i}>{linked}</span>;
        }
        return c;
      });
    }
    return children;
  }

  // PAUT-67: image annotator state. Only used when onImageAnnotated is set.
  const [annotateUrl, setAnnotateUrl] = useState<string | null>(null);

  // Heading ids: shared dedupe counter across h1/h2/h3 in this render.
  // Same logic + same source order as buildOutline() so a TOC entry's `id`
  // always matches the rendered heading's `id`. (PAUT-71)
  const headingSeen = new Map<string, number>();
  function flattenHeadingText(children: React.ReactNode): string {
    if (typeof children === "string") return children;
    if (typeof children === "number") return String(children);
    if (Array.isArray(children)) return children.map(flattenHeadingText).join("");
    if (children && typeof children === "object" && "props" in children) {
      const node = children as React.ReactElement<{ children?: React.ReactNode }>;
      return flattenHeadingText(node.props.children);
    }
    return "";
  }
  function headingRenderer(level: 1 | 2 | 3) {
    return ({ children, ...rest }: React.ComponentPropsWithoutRef<"h1">) => {
      const text = flattenHeadingText(children).trim();
      const id = `${headingIdPrefix}${nextHeadingId(text, level, headingSeen)}`;
      const Tag = (`h${level}` as unknown) as "h1" | "h2" | "h3";
      return (
        <Tag id={id} {...rest}>
          {children}
        </Tag>
      );
    };
  }

  return (
    <div className={cn("prose-doc", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkBareIssueIdentifiers, { prefixes: issuePrefixes }]]}
        urlTransform={(url, key, node) => key === "href" && node.tagName === "a"
          ? internalMarkdownUrl(url) ?? defaultUrlTransform(url) : defaultUrlTransform(url)}
        components={{
          h1: headingRenderer(1),
          h2: headingRenderer(2),
          h3: headingRenderer(3),
          table: ({ children, node: _node, ...rest }) => <div className="markdown-table-scroll" tabIndex={0} role="region" aria-label="Tabela com rolagem horizontal"><table {...rest}>{children}</table></div>,
          td: ({ children, ...rest }) => <td {...rest}>{autoLinkChildren(children)}</td>,
          li: ({ children, ...rest }) => <li {...rest}>{autoLinkChildren(children)}</li>,
          img: ({ src, alt, ...rest }) => {
            // Read-only render path — no annotate button if parent didn't
            // wire onImageAnnotated. Markdown rendered in comments / chat
            // timeline / sub-doc previews falls into this branch.
            if (!onImageAnnotated || typeof src !== "string") {
              // eslint-disable-next-line @next/next/no-img-element
              return <img src={src as string} alt={alt ?? ""} {...rest} />;
            }
            return (
              <span className="relative inline-block group">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={src} alt={alt ?? ""} {...rest} />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnnotateUrl(src);
                  }}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--bg-secondary)]/90 backdrop-blur border border-[var(--border-strong)] text-[11px] text-[var(--fg-secondary)] hover:text-[var(--fg)] opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                  title="Annotate this image"
                >
                  <Pencil size={11} />
                  Annotate
                </button>
              </span>
            );
          },
          a: ({ href, children, ...rest }) => {
            if (!href) return <span>{children}</span>;
            if (/^pauta:user:[0-9a-f-]{36}$/i.test(href ?? "")) {
              return <span className="rounded bg-[var(--accent-bg)] px-1 text-[var(--accent)]">{children}</span>;
            }
            if (href?.startsWith("wikilink:")) {
              const title = decodeInternalTarget(href.slice("wikilink:".length));
              if (!title) return <span>{children}</span>;
              const target = docs.find(
                (d) =>
                  d.workspaceId === activeWorkspaceId &&
                  d.title.toLowerCase() === title.toLowerCase()
              );
              if (!target) {
                return (
                  <span
                    className="text-[var(--fg-muted)] line-through decoration-[var(--fg-muted)]/50"
                    title="No doc with this title"
                  >
                    [[{children}]]
                  </span>
                );
              }
              return (
                <a
                  href={entityHref("doc", target.id)}
                  onClick={(event) => selectInternal(event, () => selectDoc(target.id))}
                  className="inline-flex items-center px-1 rounded bg-[var(--accent-bg)] text-[var(--accent)] hover:bg-[var(--accent-bg)]/80 no-underline border border-[var(--accent)]/30"
                  title={`Open ${target.title}`}
                >
                  {children}
                </a>
              );
            }
            if (href.startsWith("doc:")) {
              const id = decodeInternalTarget(href.slice("doc:".length));
              const target = id && docs.find((doc) => doc.workspaceId === activeWorkspaceId && doc.id.toLowerCase() === id.toLowerCase());
              if (!target) return <span className="text-[var(--fg-muted)]">{children}</span>;
              return <a href={entityHref("doc", target.id)} onClick={(event) => selectInternal(event, () => selectDoc(target.id))}
                className="text-[var(--accent)] hover:underline" title={target.title}>{children}</a>;
            }
            if (href?.startsWith("issue:")) {
              const identifier = decodeInternalTarget(href.slice("issue:".length));
              if (!identifier) return <span>{children}</span>;
              const target = issues.find(
                (i) =>
                  i.workspaceId === activeWorkspaceId &&
                  (i.identifier.toLowerCase() === identifier.toLowerCase() || i.id.toLowerCase() === identifier.toLowerCase())
              );
              if (!target) {
                return (
                  <span className="text-[var(--fg-muted)] font-mono text-[0.9em]" title="Unknown task">
                    {children}
                  </span>
                );
              }
              return (
                <a
                  href={entityHref("issue", target.id)}
                  onClick={(event) => selectInternal(event, () => selectIssue(target.id))}
                  className="inline-flex items-center gap-0.5 px-1 rounded bg-[var(--accent-bg)] text-[var(--accent)] hover:bg-[var(--accent-bg)]/80 font-mono text-[0.85em] no-underline"
                  title={target.title}
                >
                  {target.identifier}
                </a>
              );
            }
            if (href?.startsWith("project:")) {
              const name = decodeInternalTarget(href.slice("project:".length));
              if (!name) return <span>{children}</span>;
              const target = projects.find(
                (p) =>
                  p.workspaceId === activeWorkspaceId &&
                  p.name.toLowerCase() === name.toLowerCase()
              );
              if (!target) {
                return (
                  <span className="text-[var(--fg-muted)]" title="Unknown project">
                    @project:{name}
                  </span>
                );
              }
              return (
                <span
                  className="inline-flex items-center gap-0.5 px-1 rounded bg-[var(--bg-tertiary)] text-[var(--fg-secondary)] border border-[var(--border)] text-[0.9em]"
                  title={target.name}
                >
                  {target.icon} {target.name}
                </span>
              );
            }
            if (href?.startsWith("cycle:")) {
              const name = decodeInternalTarget(href.slice("cycle:".length));
              if (!name) return <span>{children}</span>;
              const target = cycles.find((c) => teams.some((team) => team.id === c.teamId && team.workspaceId === activeWorkspaceId) && c.name.toLowerCase() === name.toLowerCase());
              if (!target) {
                return (
                  <span className="text-[var(--fg-muted)]" title="Unknown cycle">
                    @cycle:{name}
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center gap-0.5 px-1 rounded bg-[var(--bg-tertiary)] text-[var(--fg-secondary)] border border-[var(--border)] text-[0.9em]">
                  ↻ {target.name}
                </span>
              );
            }
            if (href?.startsWith("user:")) {
              const handle = decodeInternalTarget(href.slice("user:".length));
              if (!handle) return <span>{children}</span>;
              const target = users.find(
                (u) =>
                  workspaceMembers.some((member) => member.workspaceId === activeWorkspaceId && member.userId === u.id) &&
                  (normalizeHandle(u.name) === normalizeHandle(handle) ||
                  normalizeHandle(u.email.split("@")[0]) === normalizeHandle(handle) ||
                  normalizeHandle(u.email.replace(/^bot\+/, "").split("@")[0]) ===
                    normalizeHandle(handle))
              );
              if (!target) {
                return (
                  <span className="text-[var(--fg-muted)]" title="Unknown user">
                    @{handle}
                  </span>
                );
              }
              const isBot = target.email.startsWith("bot+") || target.email.endsWith("@bots.pauta.dev");
              return (
                <span
                  className={
                    "inline-flex items-center gap-1 px-1 rounded text-[0.9em] no-underline " +
                    (isBot
                      ? "bg-[var(--orange)]/15 text-[var(--orange)] border border-[var(--orange)]/40"
                      : "bg-[var(--accent-bg)] text-[var(--accent)]")
                  }
                  title={target.email}
                >
                  {isBot ? "🤖" : null}
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-semibold text-white"
                    style={{ background: target.color }}
                  >
                    {target.initials}
                  </span>
                  @{target.name}
                </span>
              );
            }
            if (href?.startsWith("tag:")) {
              const tag = decodeInternalTarget(href.slice("tag:".length));
              if (!tag) return <span>{children}</span>;
              return (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTag(tag);
                  }}
                  className="inline-flex items-center px-1 rounded-full bg-[var(--bg-tertiary)] text-[var(--fg-secondary)] border border-[var(--border)] text-[0.85em] no-underline hover:border-[var(--border-strong)]"
                >
                  #{tag}
                </a>
              );
            }
            // PAUT-66: video embed for known platforms — render an iframe
            // instead of a plain link when the href matches YouTube / Loom /
            // Vimeo. CSP-safe: sandbox without `allow-same-origin` lets the
            // iframe play but can't read parent window state.
            if (href) {
              const embed = videoEmbedFromUrl(href);
              if (embed) {
                return (
                  <span className="block my-3 rounded-md overflow-hidden border border-[var(--border)] bg-black">
                    <span
                      className="block relative w-full"
                      style={{ paddingBottom: "56.25%" }}
                    >
                      <iframe
                        src={embed.src}
                        title={embed.title}
                        className="absolute inset-0 w-full h-full"
                        loading="lazy"
                        sandbox="allow-scripts allow-presentation allow-popups allow-popups-to-escape-sandbox"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        referrerPolicy="no-referrer"
                      />
                    </span>
                  </span>
                );
              }
            }
            // PAUT-66: file attachment chip for `[📎 …]` and `[🎙️ …]` links.
            // PAUT-68: voice notes get an inline `<audio controls>` so you
            // can play without leaving the doc; the chip stays as a fallback
            // download link.
            const childText = childrenToText(children);
            if (childText.startsWith("🎙️ ") && href) {
              return (
                <span className="block my-2 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border)] p-2">
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio controls preload="metadata" src={href} className="w-full max-w-md" />
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-1 text-[10.5px] text-[var(--fg-tertiary)] hover:text-[var(--fg)] no-underline"
                  >
                    {childText}
                  </a>
                </span>
              );
            }
            if (childText.startsWith("📎 ")) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2 py-1 my-1 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] text-[var(--fg-secondary)] no-underline transition-colors"
                  title="Click to download"
                >
                  {childText}
                </a>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                {children}
              </a>
            );
          },
        }}
      >
        {transformed}
      </ReactMarkdown>
      {"presentationChanged" in projection && projection.presentationChanged && <details className="mt-6 rounded-md border border-[var(--border)] p-3" onClick={(event) => event.stopPropagation()}>
        <summary className="cursor-pointer text-[12px] font-medium text-[var(--fg-secondary)]">Ver conteúdo preservado</summary>
        <div className="mt-3"><Markdown content={projection.originalBody} headingIdPrefix={`${headingIdPrefix}preserved-`} referencedIssues={referencedIssues} recognizeBareIssues={recognizeBareIssues} /></div>
      </details>}
      {projection.history.length > 0 && <section className="mt-6 border-t border-[var(--border)] pt-3" onClick={(event) => event.stopPropagation()}>
        <button type="button" aria-expanded={historyOpen} onClick={() => setHistorySource(historyOpen ? null : content)}
          className="min-h-11 rounded-md border border-[var(--border)] px-3 text-[12px] font-medium text-[var(--fg-secondary)]">
          {historyOpen ? "Ocultar histórico" : "Ver histórico preservado"} ({projection.history.length})
        </button>
        {historyOpen && <div className="mt-3 space-y-4">{projection.history.map((revision, index) => <section key={revision.key} className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
          <p className="mb-2 text-[11px] font-medium text-[var(--fg-muted)]">Versão preservada {index + 1}</p>
          <Markdown content={revision.body} headingIdPrefix={`history-${revision.key}-`} referencedIssues={referencedIssues} recognizeBareIssues={false} />
        </section>)}</div>}
      </section>}
      {onImageAnnotated && (
        <ImageAnnotator
          open={annotateUrl !== null}
          imageUrl={annotateUrl ?? ""}
          onCancel={() => setAnnotateUrl(null)}
          onSave={(newUrl) => {
            if (annotateUrl) onImageAnnotated(annotateUrl, newUrl);
            setAnnotateUrl(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Detect a YouTube/Loom/Vimeo URL and return the embed src + a human title.
 * Returns null for everything else (the link falls through to a normal `<a>`).
 * PAUT-66.
 */
function videoEmbedFromUrl(href: string): { src: string; title: string } | null {
  try {
    const u = new URL(href);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube — youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      if (id) return { src: `https://www.youtube.com/embed/${id}`, title: "YouTube video" };
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return { src: `https://www.youtube.com/embed/${v}`, title: "YouTube video" };
      const m = u.pathname.match(/^\/embed\/([^/]+)/);
      if (m) return { src: `https://www.youtube.com/embed/${m[1]}`, title: "YouTube video" };
    }
    // Loom — loom.com/share/<id> or loom.com/embed/<id>
    if (host === "loom.com" || host === "www.loom.com") {
      const m = u.pathname.match(/^\/(?:share|embed)\/([^/?]+)/);
      if (m) return { src: `https://www.loom.com/embed/${m[1]}`, title: "Loom recording" };
    }
    // Vimeo — vimeo.com/<id> or vimeo.com/channels/.../<id>
    if (host === "vimeo.com" || host === "player.vimeo.com") {
      if (host === "player.vimeo.com") {
        return { src: href, title: "Vimeo video" };
      }
      const segs = u.pathname.split("/").filter(Boolean);
      const id = segs[segs.length - 1];
      if (id && /^\d+$/.test(id)) {
        return { src: `https://player.vimeo.com/video/${id}`, title: "Vimeo video" };
      }
    }
  } catch {
    // not a URL → not embeddable
  }
  return null;
}

/**
 * React children → flattened text so we can sniff link labels for the
 * `📎 …` / `🎙️ …` attachment-chip pattern emitted by MarkdownEditor.
 * PAUT-66.
 */
function childrenToText(children: unknown): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return childrenToText((children as { props: { children: unknown } }).props.children);
  }
  return "";
}

function preprocess(content: string): string {
  // We work line-by-line so we can leave code-fences / inline code alone.
  const lines = content.split("\n");
  let inFence = false;
  const out: string[] = [];
  for (const line of lines) {
    if (/^```/.test(line.trim())) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    // Preserve inline code spans — split on backticks, only transform odd segments
    const parts = line.split(/(`[^`]*`|@\[[^\]\r\n]{1,100}\]\(pauta:user:[0-9a-f-]{36}\))/gi);
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 1) continue; // inline code span
      let p = parts[i];
      p = p.replace(/\[\[([^\]]+?)\]\]/g, (_m, t) => {
        const title = String(t).trim();
        return `[${title}](wikilink:${encodeURIComponent(title)})`;
      });
      // Issue mention: @ABC-123 — 2-5 uppercase letters then digits
      p = p.replace(/(^|[\s(])@([A-Z]{2,5}-\d+)/g, (_m, pre, ident) => {
        return `${pre}[${ident}](issue:${encodeURIComponent(ident)})`;
      });
      // Project mention: @project:Name (stop at whitespace or , . ; ! ? ])
      p = p.replace(/(^|[\s(])@project:([^\s,.;!?\])]+(?:\s+[^\s,.;!?\])]+)*)/g, (_m, pre, name) => {
        const n = String(name).trim();
        return `${pre}[@project:${n}](project:${encodeURIComponent(n)})`;
      });
      p = p.replace(/(^|[\s(])@cycle:([^\s,.;!?\])]+(?:\s+[^\s,.;!?\])]+)*)/g, (_m, pre, name) => {
        const n = String(name).trim();
        return `${pre}[@cycle:${n}](cycle:${encodeURIComponent(n)})`;
      });
      // User/bot mention: @lowercase-handle (does NOT match @ABC-123 which
      // is already captured above, and does NOT match @project: / @cycle:
      // prefixes which were consumed earlier). Names may contain letters,
      // digits, dash, underscore, dot.
      p = p.replace(/(^|[\s(])@([a-z][a-z0-9._-]{1,60})\b/g, (match, pre, handle) => {
        return `${pre}[@${handle}](user:${encodeURIComponent(handle)})`;
      });
      // Tag: #tagname. Must NOT be at line start followed by space (that's a heading).
      // Allow preceded by whitespace, '(', or start-of-segment when not heading.
      p = p.replace(/(^|[\s(])#([a-zA-Z][a-zA-Z0-9_-]{1,40})\b/g, (match, pre, tag) => {
        // Heuristic: if this is the start of the line and the next char after tag is a space,
        // it could be a heading. But real markdown headings need the # immediately at
        // line-start. We already skip full-line headings below.
        return `${pre}[#${tag}](tag:${encodeURIComponent(tag)})`;
      });
      parts[i] = p;
    }
    // But don't rewrite if the whole line is a markdown heading (starts with # then space)
    const joined = parts.join("");
    const origHeadingMatch = /^(\s*)(#{1,6})\s/.exec(line);
    if (origHeadingMatch) {
      // Line was a heading. Keep original to avoid turning #H1 into tag link.
      out.push(line);
    } else {
      out.push(joined);
    }
  }
  return out.join("\n");
}

/**
 * Strip a single leading emoji (any non-ASCII char or combined emoji
 * sequence) plus surrounding whitespace, lowercase, collapse internal
 * whitespace. Used to fuzzy-match table-cell text against doc titles.
 *
 *   "🏗️ Architecture & Stack" → "architecture & stack"
 *   "  📊 Data Model "          → "data model"
 */
function normalizeDocTitle(s: string): string {
  return s
    .trim()
    // Strip a leading emoji cluster (anything outside basic latin) + space.
    // Conservative: only strip if followed by a space, so plain text like
    // "Café" or "naïve" doesn't lose its first letter.
    .replace(/^[^ -~]+\s+/u, "")
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeHandle(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
